// Cognito hosted UI (auth.{baseDomain}), authorization-code flow + PKCE, no SDK. Each tenant host has its own app
// client, which /api/public/site names; federated tenants go straight to their IdP (identity_provider=t-<slug>).
// Tokens live in sessionStorage (gone when the tab closes); the 12-hour refresh token renews them quietly.

export interface AppConfig {
  region: string;
  stage: string;
  baseDomain: string;
  userPoolId: string;
  loginDomain: string;
  platformClientId: string;
}
export type SiteInfo =
  | { kind: "platform"; clientId: string }
  | { kind: "tenant"; clientId: string; tenant: { tenantId: string; name: string; signInMode: "cognito" | "federated" }; identityProvider?: string; portalSignIn?: boolean; setupPending?: boolean };

interface Tokens {
  idToken: string;
  refreshToken?: string;
  expiresAt: number;
}

let cfg: AppConfig | null = null;
let site: SiteInfo | null = null;
const TOKENS = "iap-auth";
const PENDING = "iap-auth-pending";
const redirectUri = () => `${location.origin}/auth/callback`;

const safe = <T,>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export class NotFound extends Error {}

/** config.json (per stage) + /api/public/site (per host). Unknown tenants throw NotFound. */
export async function boot(): Promise<{ config: AppConfig; site: SiteInfo }> {
  const r = await fetch("/config.json", { cache: "no-store" });
  if (!r.ok) throw new Error("config.json is missing");
  cfg = (await r.json()) as AppConfig;
  const s = await fetch("/api/public/site", { cache: "no-store" });
  if (s.status === 404) throw new NotFound("Not found");
  if (!s.ok) throw new Error(`The portal isn't reachable right now (${s.status}).`);
  site = (await s.json()) as SiteInfo;
  return { config: cfg, site };
}
export const siteInfo = () => site!;
export const signInReady = () => Boolean(site);

const read = (): Tokens | null => safe(() => JSON.parse(sessionStorage.getItem(TOKENS) ?? "null"), null);
const write = (t: Tokens | null) => safe(() => (t ? sessionStorage.setItem(TOKENS, JSON.stringify(t)) : sessionStorage.removeItem(TOKENS)), undefined);
export const signedIn = () => Boolean(read());

/** After signIn()'s /logout hop: where to continue and which way in, or null. Stale markers (over 2 minutes) are dropped. */
export function pendingFreshSignIn(): { back: string; portal: boolean } | null {
  const f = safe(() => JSON.parse(sessionStorage.getItem(FRESH) ?? "null"), null) as { back: string; at: number; portal?: boolean } | null;
  if (!f) return null;
  if (Date.now() - f.at > 120_000) {
    safe(() => sessionStorage.removeItem(FRESH), undefined);
    return null;
  }
  return { back: f.back, portal: !!f.portal };
}

const b64url = (b: Uint8Array) => btoa(String.fromCharCode(...b)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const random = (n = 32) => b64url(crypto.getRandomValues(new Uint8Array(n)));
const challenge = async (v: string) => b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v))));

/** Trigger refusals arrive as "PreTokenGeneration failed with error <ours>. (Service: …; Request ID: …)". Keep <ours>. */
const cleanError = (desc: string) =>
  desc
    .replace(/^\w+ failed with error\s*/i, "")
    .replace(/\s*\(Service: AWSCognito[\s\S]*$/, "")
    .replace(/\.+$/, "") || "Sign-in failed";

async function token(body: Record<string, string>) {
  const res = await fetch(`https://${cfg!.loginDomain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: site!.clientId, ...body }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(cleanError(d.error_description ?? d.error ?? ""));
  return d;
}
const toTokens = (d: any, keep?: string): Tokens => ({ idToken: d.id_token, refreshToken: d.refresh_token ?? keep, expiresAt: Date.now() + Number(d.expires_in ?? 3600) * 1000 });

const FRESH = "iap-auth-fresh";

/**
 * auth.{baseDomain} is shared by every tenant and the platform console, and Cognito's classic sign-in page silently
 * reuses whoever is already signed in there (it ignores prompt=login). So a sign-in first visits /logout to clear
 * that session, comes back to this page, and only then goes to /oauth2/authorize.
 */
export async function signIn(back?: string, opts: { portal?: boolean } = {}) {
  if (!signInReady()) return;
  const fresh = safe(() => sessionStorage.getItem(FRESH), null);
  if (!fresh) {
    safe(() => sessionStorage.setItem(FRESH, JSON.stringify({ back: back ?? location.pathname + location.search, at: Date.now(), portal: !!opts.portal })), undefined);
    location.assign(`https://${cfg!.loginDomain}/logout?${new URLSearchParams({ client_id: site!.clientId, logout_uri: `${location.origin}/` })}`);
    return;
  }
  safe(() => sessionStorage.removeItem(FRESH), undefined);
  const verifier = random(48);
  const state = random(12);
  safe(() => sessionStorage.setItem(PENDING, JSON.stringify({ verifier, state, back: back ?? location.pathname + location.search })), undefined);
  const q = new URLSearchParams({
    client_id: site!.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: redirectUri(),
    state,
    code_challenge: await challenge(verifier),
    code_challenge_method: "S256",
    // With portal: true (only offered while SSO isn't enforced) Cognito's own sign-in page is used instead of the IdP.
    ...(site!.kind === "tenant" && site!.identityProvider && !opts.portal ? { identity_provider: site!.identityProvider } : {}),
  });
  location.assign(`https://${cfg!.loginDomain}/oauth2/authorize?${q}`);
}

/** On /auth/callback: finishes the sign-in. Returns an error message or null. */
export async function completeSignIn(): Promise<string | null> {
  if (location.pathname !== "/auth/callback") return null;
  const p = new URLSearchParams(location.search);
  const pending = safe(() => JSON.parse(sessionStorage.getItem(PENDING) ?? "null"), null) as { verifier: string; state: string; back: string } | null;
  safe(() => sessionStorage.removeItem(PENDING), undefined);
  history.replaceState(null, "", pending?.back && pending.back !== "/auth/callback" ? pending.back : "/");
  if (p.has("error")) {
    // Trigger refusals come back as "PreSignUp failed with error <message>." / "PreTokenGeneration failed with error …".
    return p.get("error_description") ? cleanError(p.get("error_description")!) : "Sign-in was cancelled";
  }
  if (!pending || pending.state !== p.get("state") || !p.get("code")) return "That sign-in link expired. Please sign in again.";
  try {
    write(toTokens(await token({ grant_type: "authorization_code", code: p.get("code")!, redirect_uri: redirectUri(), code_verifier: pending.verifier })));
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

let refreshing: Promise<Tokens | null> | null = null;
export async function idToken(): Promise<string | null> {
  let t = read();
  if (!t) return null;
  if (t.expiresAt - Date.now() < 60_000) {
    const cur = t;
    refreshing ??= (cur.refreshToken ? token({ grant_type: "refresh_token", refresh_token: cur.refreshToken }).then((d) => toTokens(d, cur.refreshToken)) : Promise.resolve(null))
      .catch(() => null)
      .then((n) => {
        write(n);
        return n;
      })
      .finally(() => (refreshing = null));
    t = await refreshing;
  }
  return t?.idToken ?? null;
}

export async function signOut() {
  const t = read();
  write(null);
  if (t?.refreshToken)
    await fetch(`https://${cfg!.loginDomain}/oauth2/revoke`, { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body: new URLSearchParams({ token: t.refreshToken, client_id: site!.clientId }) }).catch(() => undefined);
  location.assign(`https://${cfg!.loginDomain}/logout?${new URLSearchParams({ client_id: site!.clientId, logout_uri: `${location.origin}/` })}`);
}
